import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import HelpCard from '../../../components/help-card/ui/HelpCard';
import FormDialog from '../../../components/form-dialog/ui/FormDialog';
import { useLanguage } from '../../../../context/LanguageContext';
import { Credentials } from '../../../../assets/features/Credentials';
import { seoData } from '../../../../data/seoData';
import { initializeAppointmentFormHandler } from '../../../components/email/service/appointmentFormHandler';
import wecallBgImage from '../../../../assets/logo/wecall/wecall2.webp';
import wecallImage from '../../../../assets/logo/wecall/wecall.webp';
import { trackPhoneClick, trackWhatsAppClick } from '../../../components/email/utils/trackingUtils';
import '../style/contact.css';

const ContactPage: React.FC = () => {
    const { t, language } = useLanguage();
    const formRef = useRef<HTMLFormElement>(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        privacyPolicy: false
    });
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [_recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message?: string;
    }>({
        isOpen: false,
        type: 'success'
    });

    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);

        // Debug: Check if reCAPTCHA script is loaded
        
        const checkRecaptcha = () => {
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                
                // Debug: Check if reCAPTCHA is ready
                window.grecaptcha.ready(() => {
                    
                    // Manually render the reCAPTCHA widget
                    setTimeout(() => {
                        const recaptchaContainer = document.querySelector('.g-recaptcha');
                        if (recaptchaContainer && !recaptchaContainer.querySelector('iframe')) {
                            try {
                                const widgetId = window.grecaptcha.render(recaptchaContainer as HTMLElement, {
                                    'sitekey': Credentials.RECAPTCHA_SITE_KEY,
                                    'theme': 'light',
                                    'size': 'normal'
                                });
                                setRecaptchaWidgetId(widgetId);
                                
                                // Verify rendering
                                setTimeout(() => {
                                    const iframe = recaptchaContainer.querySelector('iframe');
                                    if (iframe) {
                                    } else {
                                    }
                                }, 500);
                            } catch (_) {
                                setRecaptchaError('Failed to render reCAPTCHA widget');
                            }
                        } else if (recaptchaContainer && recaptchaContainer.querySelector('iframe')) {
                        } else {
                        }
                    }, 500);
                });
            } else {
            }
        };

        // Initial check
        checkRecaptcha();

        // Retry mechanism - check every 500ms for up to 10 seconds
        let attempts = 0;
        const maxAttempts = 20;
        const checkInterval = setInterval(() => {
            attempts++;
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                setRecaptchaError(null);
                clearInterval(checkInterval);
            } else if (attempts >= maxAttempts) {
                // reCAPTCHA is consent-gated: while the gate notice is shown
                // the script is intentionally absent — keep polling instead of
                // reporting a loading failure.
                if (!document.querySelector('.recaptcha-consent-note')) {
                    setRecaptchaError('reCAPTCHA failed to load. Please refresh the page.');
                    clearInterval(checkInterval);
                }
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, []);

    // Additional check after component fully renders
    useEffect(() => {
        const checkWidget = () => {
            const recaptchaContainer = document.querySelector('.g-recaptcha');
            if (recaptchaContainer) {
                const iframe = recaptchaContainer.querySelector('iframe');
                if (!iframe) {
                } else {
                }
            } else {
            }
        };

        // Check immediately
        checkWidget();
        
        // Check after a delay to catch late renders
        const timeout = setTimeout(checkWidget, 2000);
        
        return () => clearTimeout(timeout);
    }, [recaptchaLoaded]);

    // Expose dialog function to window for use by appointment form handler
    useEffect(() => {
        const showContactDialog = (type: 'success' | 'error', message?: string) => {
            setDialogState({
                isOpen: true,
                type,
                message
            });
        };

        // Expose function to window
        (window as any).showContactDialog = showContactDialog;

        return () => {
            // Cleanup
            delete (window as any).showContactDialog;
        };
    }, []);

    // Initialize appointment form handler for email sending
    useEffect(() => {
        if (formRef.current) {
            // Wait a bit for the form to be fully rendered
            setTimeout(() => {
                initializeAppointmentFormHandler(formRef.current!, language || 'pt');
            }, 500);
        }
    }, [language]);

    const handleCloseDialog = () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Form submission is handled by initializeAppointmentFormHandler
        // This preventDefault allows the handler to process the form
        // The handler will validate and send emails automatically
    };

    return (
        <>
            <SEO 
                title={seoData.contact.title}
                description={seoData.contact.description}
                keywords={seoData.contact.keywords}
                canonical={seoData.contact.canonical}
            />
        <div className="contact-page-wrapper">
            <Navbar />
            
            {/* Page Header */}
            <section className="page-header contact-page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: `url(${wecallBgImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <div className="page-header__img-1">
                            <img loading="lazy" 
                                src={wecallImage} 
                                alt="Roady Chaves" 
                                style={{ width: '250px', height: '300px', objectFit: 'contain' }}
                            />
                        </div>
                        <h1>
                            {language === 'pt' ? 'Contactos do Roady Chaves' : 'Roady Chaves Contacts'}
                        </h1>
                        {/* Opening hours as text, right under the H1 (A7) */}
                        <p
                            className="contact-page-header__hours"
                            dangerouslySetInnerHTML={{
                                __html: `${(t as any).CONTACT?.hours || 'Horário de Funcionamento:'} ${(t as any).CONTACT?.schedule || 'Seg - Sex: 09:00–12:30, 14:00–19:00<br>Sáb: 09:00–12:30, 14:00–18:30<br>Dom: 09:00–13:00'}`
                            }}
                        ></p>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{t.NAVIGATION?.start || 'Início'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{(t.NAVIGATION as any)?.contact || 'Contacte-nos'}</li>
                            </ul>
                        </div>
                        {/* Phone call button above the fold (A6) */}
                        <a
                            href="tel:+351276249290"
                            className="thm-btn page-header__call-btn"
                            onClick={() => trackPhoneClick('contact_page')}
                        >
                            <i className="icon-phone-call"></i>
                            {language === 'pt' ? 'Fale connosco' : 'Call us'}
                        </a>
                    </div>
                </div>
            </section>

            {/* Contact Page */}
            <section className="contact-page">
                <div className="container">
                    {/* Contact info: address, hours, phone, WhatsApp, quote (B7) */}
                    <div className="contact-page__info">
                        <div className="contact-page__info-item">
                            <h2 className="contact-page__info-title">
                                {(t as any).CONTACT?.location || 'Localização:'}
                            </h2>
                            <p
                                className="contact-page__info-text"
                                dangerouslySetInnerHTML={{
                                    __html: (t as any).CONTACT?.address || 'Centro Auto Roady Chaves<br>R. Joaquim Mazarém 28, 5400-695 Chaves'
                                }}
                            ></p>
                            <p className="contact-page__info-text">
                                {language === 'pt'
                                    ? 'Estamos na R. Joaquim Mazarém 28, em Chaves, com estacionamento à porta.'
                                    : 'We are at R. Joaquim Mazarém 28, in Chaves, with parking at the door.'}
                            </p>
                        </div>
                        <div className="contact-page__info-buttons">
                            <a
                                href="tel:+351276249290"
                                className="thm-btn contact-page__info-btn"
                                onClick={() => trackPhoneClick('contact_page')}
                            >
                                {language === 'pt' ? 'Fale connosco' : 'Call us'} · (+351) 276 249 290
                                <span><i className="icon-phone-call"></i></span>
                            </a>
                            {/* WhatsApp (A8) — number from the site's existing WhatsApp button */}
                            <a
                                href="https://wa.me/351276249290"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="thm-btn contact-page__info-btn contact-page__info-btn--whatsapp"
                                onClick={() => trackWhatsAppClick('contact_page')}
                            >
                                WhatsApp
                                <span><i className="fab fa-whatsapp"></i></span>
                            </a>
                            <Link to="/orcamento" className="thm-btn contact-page__info-btn">
                                {language === 'pt' ? 'Peça orçamento' : 'Request a Quote'}
                                <span><i className="icon-next"></i></span>
                            </Link>
                        </div>
                        <p className="contact-page__info-note">
                            {(t as any).CONTACT?.phoneCallInfo || 'Chamada para a rede fixa nacional'}
                        </p>
                    </div>

                    <div className="contact-page__form-container">
                        <div className="contact-page__form-box">
                            <div className="contact-page__form-wrapper">
                                <div className="contact-page__form-left">
                                    <img loading="lazy" 
                                        src="/assets/logo/contact/chaves_img_form_430 x 460.webp" 
                                        alt="Roady Chaves" 
                                        style={{ width: '600px', height: '640px', objectFit: 'cover' }}
                                    />
                                </div>
                                
                                <div className="contact-page__form-right">
                                    <div className="contact-page__appointment-form">
                                        <div className="section-title text-left sec-title-animation animation-style2">
                                            <div className="section-title__tagline-box">
                                                <div className="section-title__tagline-border">
                                                    <div className="section-title__shape-1">
                                                        <i className="section-title__circle"></i>
                                                    </div>
                                                </div>
                                                <h6 className="section-title__tagline">Entre em Contacto</h6>
                                                <div className="section-title__tagline-border">
                                                    <div className="section-title__shape-2">
                                                        <i className="section-title__circle"></i>
                                                    </div>
                                                </div>
                                            </div>
                                            <h3 className="section-title__title title-animation">{(t.NAVIGATION as any)?.contact || 'Contacte-nos'}</h3>
                                        </div>
                                        
                                        <form 
                                            ref={formRef}
                                            className="contact-form-validated appointment-one__form"
                                            onSubmit={handleSubmit}
                                            noValidate
                                        >
                                            <input type="hidden" name="language" id="contact-language" value="pt" />
                                            <div className="row">
                                                <div className="col-xl-6 col-lg-6 col-md-6">
                                                    <div className="appointment-one__input-box">
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            placeholder="Nome Completo"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 col-lg-6 col-md-6">
                                                    <div className="appointment-one__input-box">
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            placeholder="Endereço de Email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 col-lg-6 col-md-6">
                                                    <div className="appointment-one__input-box">
                                                        <input
                                                            type="text"
                                                            name="phone"
                                                            placeholder="Número de Telefone"
                                                            value={formData.phone}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 col-lg-6 col-md-6">
                                                    <div className="appointment-one__input-box">
                                                        <div className="select-box">
                                                            <select
                                                                className="selectmenu wide"
                                                                name="service"
                                                                id="service-select"
                                                                value={formData.service}
                                                                onChange={handleInputChange}
                                                            >
                                                                <option value="">Selecione um Serviço</option>
                                                                <option value="general_mechanics">Mecânica Geral</option>
                                                                <option value="air_conditioning">Ar Condicionado</option>
                                                                <option value="alignment">Alinhamento</option>
                                                                <option value="batteries">Baterias</option>
                                                                <option value="brakes">Travões</option>
                                                                <option value="diagnostic_test">Teste Diagnóstico</option>
                                                                <option value="workshop_package">Pack Serviços Oficina</option>
                                                                <option value="washing">Lavagem</option>
                                                                <option value="headlight_restoration">Restauro de Faróis</option>
                                                                <option value="inspection">Inspeção</option>
                                                                <option value="roady_review">Revisão Roady</option>
                                                                <option value="shock_absorbers">Amortecedores</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xl-12">
                                                    <div className="appointment-one__input-box">
                                                        <textarea
                                                            name="message"
                                                            placeholder="A Sua Mensagem"
                                                            rows={6}
                                                            value={formData.message}
                                                            onChange={handleInputChange}
                                                            required
                                                        ></textarea>
                                                    </div>
                                                </div>
                                                <div className="col-xl-12">
                                                    <div className="appointment-one__checkbox-box">
                                                        <div className="appointment-one__checkbox">
                                                            <input
                                                                type="checkbox"
                                                                id="privacyPolicyContact"
                                                                name="privacyPolicy"
                                                                checked={formData.privacyPolicy}
                                                                onChange={handleInputChange}
                                                                required
                                                            />
                                                            <label htmlFor="privacyPolicyContact" id="privacy_policy_contact_label">
                                                                <span className="appointment-one__checkbox-mark"></span>
                                                                Concordo com a{' '}
                                                                <a 
                                                                    href="/politica-privacidade" 
                                                                    className="privacy-policy-link-red"
                                                                >
                                                                    Política de Privacidade
                                                                </a>
                                                                {' '}e autorizo o tratamento dos meus dados pessoais.
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xl-12">
                                                    <div className="appointment-one__recaptcha-btn-row">
                                                        <div className="appointment-one__recaptcha-box">
                                                            <div className="g-recaptcha" data-sitekey={Credentials.RECAPTCHA_SITE_KEY}></div>
                                                            {/* Debug info */}
                                                            {!recaptchaLoaded && !recaptchaError && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                                    Loading reCAPTCHA...
                                                                </div>
                                                            )}
                                                            {recaptchaError && (
                                                                <div style={{ fontSize: '12px', color: '#d90202', marginTop: '5px' }}>
                                                                    ⚠️ {recaptchaError}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="appointment-one__btn-box">
                                                            <button 
                                                                type="submit" 
                                                                id="contact-submit-btn" 
                                                                className="thm-btn appointment-now-btn contact-us-btn"
                                                            >
                                                                Enviar Mensagem
                                                                <span className="btn-arrow"><i className="icon-next"></i></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                        <div className="result"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Help Card Section */}
                    <HelpCard />
                </div>
            </section>

            <Footer />
            <ScrollToTop />
            <FormDialog
                isOpen={dialogState.isOpen}
                type={dialogState.type}
                message={dialogState.message}
                onClose={handleCloseDialog}
            />
        </div>
        </>
    );
};

export default ContactPage;

